// @flow

import * as React from 'react';

import clsx from 'clsx';
import { useDispatch } from 'react-redux';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import { selectContracts, approveContract, type Contract } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Base, CheckBox, Svg } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';
import { selectShopCurrent } from 'src/modules/organization/selectors';

import { ContractsButton } from './ContractsButton';

import styles from './Contracts.scss';

type RefType = HTMLDivElement;

function Contracts(
  props: {},
  ref: React$RefSetter<RefType | null>,
): null | React.MixedElement {
  const { __ } = useTranslator();
  const dispatch = useDispatch();

  const contracts = useAppSelector(selectContracts);
  const isAuthenticated = useAppSelector(selectIsAuthenticatedUser);
  const currentOrg = useAppSelector(state =>
    // $FlowFixMe[incompatible-call]: flow does't recognize multiple declarations of the function
    selectShopCurrent(state, isAuthenticated),
  );

  const willMount = React.useRef(true);
  if (willMount.current) {
    contracts.forEach(contract => {
      dispatch(
        approveContract({
          identifier: contract.identifier,
          isApproved: false,
        }),
      );
    });
    willMount.current = false;
  }

  const handleContractCheckbox = (evt: any) => {
    dispatch(
      approveContract({
        identifier: evt.currentTarget.name,
        isApproved: evt.currentTarget.checked,
      }),
    );
  };

  const renderContract = (contract: Contract) => (
    <div key={contract.identifier} className={styles.contractRow}>
      <CheckBox
        classes={{ label: styles.chekboxLabel }}
        name={contract.identifier}
        onChange={handleContractCheckbox}
        checked={contract.isApproved}
      >
        <span>
          {contract.href ? (
            <a
              className={styles.link}
              href={contract.href}
              target="_blank"
              rel="noopener"
            >
              {contract.name}
            </a>
          ) : (
            contract.name
          )}
          {!!contract.registry_contract_href && (
            <a
              key={`registry-contact-href-${contract.name}`}
              className={styles.link}
              href={contract.registry_contract_href}
              target="_blank"
              rel="noopener"
            >
              {' '}
              {__('and Registry contract')}
            </a>
          )}
        </span>
      </CheckBox>
    </div>
  );

  const acceptText = __('Please read and accept the following contracts:');

  const hasContracts = Boolean(Array.isArray(contracts) && contracts.length);
  const allContractsApproved = contracts.every(contract => contract.isApproved);

  const shouldDisplayVATWarning =
    currentOrg?.type === 'individual' && currentOrg?.country === 'FR';

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div
      ref={ref}
      className={clsx(
        styles.container,
        allContractsApproved ? styles.containerAllChecked : undefined,
      )}
    >
      <h3 className={styles.title}>
        <Svg id="document" name="document" />
        {allContractsApproved
          ? __(
              '<for-translators-use>%(count)s</for-translators-use>Contracts accepted',
              { count: contracts.length },
            )
          : __(
              '<for-translators-use>%(count)s</for-translators-use>Contracts to accept',
              { count: contracts.length },
            )}
      </h3>

      {hasContracts && (
        <div>
          <p className={styles.info}> {acceptText} </p>

          <ContractsButton />

          <div className={styles.contracts}>
            {hasContracts && contracts.map(renderContract)}
          </div>

          {shouldDisplayVATWarning && (
            <Base tagName="p" margin="none" marginBottom="xsmall">
              {__(
                "Offering services delivered electronically to non-taxable persons whose permanent or usual residence is within the territory of the European Union is subject to VAT in the buyer's state of residence. The information provided in your user account, then, should match those of your permanent or usual residence.",
              )}
            </Base>
          )}
        </div>
      )}

      {!hasContracts && <div>{__('No contracts available.')}</div>}
    </div>
  );
}

const ContractsForwarded: React.ComponentType<{}> = React.forwardRef<
  {},
  RefType | null,
>(Contracts);

export default ContractsForwarded;
